import _ from 'lodash';
import moment from 'moment';
import type { BaseResponseDto } from 'types/api/baseResponseDto';
import type { BusinessUnitSessionDto } from 'types/api/businessUnitSessionDto';
import type { CreateUserRequestDto } from 'types/api/createUserRequestDto';
import type { FeatureFlagValues } from 'types/api/featureFlags';
import type { NameIdPair } from 'types/api/nameIdPair';
import type { OrganizationSessionDto } from 'types/api/organizationSessionDto';
import type { TenantUserRoleJpaDto } from 'types/api/tenantUserRoleJpaDto';
import type { TenantUserRoleResponseDto } from 'types/api/tenantUserRoleResponseDto';
import type { UpdateUserRequest } from 'types/api/updateUserRequest';
import type {
  AuditLogFiltersResponse,
  AuditLogResponseDto,
  LoginAttemptResponseDto,
  LoginAttemptsFilterResponse,
  UpdateLoginAttemptParams,
  UserEnabledDisabledResponse,
  UserFiltersResponse,
  UserPreferenceUpdateRequest,
  UserResponseShort,
  UserStatsAllPayload,
  UserStatsAllResponse,
  UserStatsMetricPayload,
  UserStatsMetricResponse,
  UsersInAccessibleTeamsRequest,
} from 'types/api/user';
import type { UserInfoProjectorImpl } from 'types/api/userInfoProjectorImpl';
import type { UserJpaDto } from 'types/api/userJpaDto';
import type { UserResponseTenantDto } from 'types/api/userResponseTenantDto';
import type { UserSessionDto } from 'types/api/userSessionDto';
import type { PageResponse } from 'types/pageResponse';
import { getFindingAPIDurationFilter } from 'utils/date/filters';
import { getStartAndEndDateForQuickOption } from 'utils/date/quickOptions';
import { toUserStatsApiFilters } from 'utils/usersWidgetUtils';

import APIClient, { failSafe, makeCancellableAPIRequest } from './index';

const UserAPI = {
  getAll(productId?: number, accessLevel?: string) {
    const params: Record<string, unknown> = {};
    if (productId) params.productId = productId;
    // accessLevel possible values undefined,TEAM,TENANT
    if (accessLevel) params.accessLevel = accessLevel;

    return makeCancellableAPIRequest<UserInfoProjectorImpl[]>(
      'GET',
      '/user/get-users',
      { params },
    );
  },
  getUsers(data: {
    filters: Record<string, unknown>;
    page?: number;
    size?: number;
    sort?: string;
    sortColumn?: string;
    sortOrder?: string;
    [key: string]: unknown;
  }) {
    const { filters, ...restParams } = data;
    const queryParams = new URLSearchParams(
      _.omitBy(restParams, _.isNil) as Record<string, string>,
    );
    if (!data.sort || !data.sortColumn) delete data.sort;
    if (!data.sortOrder) delete data.sortOrder;
    if (
      (filters?.lastlogin as string[] | undefined) &&
      (filters.lastlogin as string[]).length === 1
    ) {
      filters.lastLogin = getStartAndEndDateForQuickOption({
        label: (filters.lastlogin as string[])[0],
        utcString: true,
      });
      delete filters.lastlogin;
    } else {
      filters.lastLogin = filters.lastlogin;
      delete filters.lastlogin;
    }

    return makeCancellableAPIRequest<
      BaseResponseDto<PageResponse<UserResponseTenantDto>>
    >('POST', `/api/v2/user/search?${queryParams.toString()}`, {
      data: filters,
    });
  },
  validate(email: { email: string } = { email: '' }) {
    return makeCancellableAPIRequest<BaseResponseDto<boolean>>(
      'POST',
      '/api/v2/user/email/availability',
      {
        data: email,
      },
      false,
    );
  },
  getUsersFilters() {
    return makeCancellableAPIRequest<BaseResponseDto<UserFiltersResponse>>(
      'GET',
      '/api/v2/user/filters',
    );
  },
  getUserEnabledDisabled() {
    return makeCancellableAPIRequest<UserEnabledDisabledResponse>(
      'GET',
      '/api/user/count',
    );
  },
  addUser(data: CreateUserRequestDto) {
    return makeCancellableAPIRequest<NameIdPair>('POST', '/user/add/user', {
      data: data,
    });
  },
  updateUser(data: UpdateUserRequest) {
    return makeCancellableAPIRequest<void>('PUT', '/user/update/user', {
      data: data,
    });
  },
  resetPassword(data: { email?: string; userId?: number }) {
    return makeCancellableAPIRequest<string>('POST', '/user/reset/password', {
      data: data,
    });
  },
  deleteUser(userId: number) {
    return makeCancellableAPIRequest<void>(
      'DELETE',
      `/user/data/remove/${userId}`,
    );
  },
  bulkUserOperation(data: unknown) {
    return makeCancellableAPIRequest<BaseResponseDto<unknown>>(
      'POST',
      '/api/v2/user/bulk',
      {
        data: data,
      },
    );
  },
  updateTenant(tenantId: number, businessUnitId: number) {
    return makeCancellableAPIRequest<UserSessionDto>(
      'GET',
      `/user/change/account/${tenantId}/${businessUnitId}`,
    );
  },
  getRoles() {
    return makeCancellableAPIRequest<TenantUserRoleResponseDto[]>(
      'GET',
      '/user/roles',
    );
  },
  getAssignableRolesForBU(id: number, attr = 'businessUnitId') {
    return makeCancellableAPIRequest<TenantUserRoleJpaDto[]>(
      'GET',
      '/user/assignable/roles',
      {
        params: {
          [attr]: id,
        },
      },
    );
  },
  getAssignableRoles(params?: Record<string, unknown>) {
    return makeCancellableAPIRequest<TenantUserRoleJpaDto[]>(
      'GET',
      '/user/assignable/roles',
      { params },
    );
  },
  getCurrentUserRole() {
    return makeCancellableAPIRequest<string[]>('GET', '/user/details/role');
  },
  getBusinessUnit() {
    return makeCancellableAPIRequest<BusinessUnitSessionDto[]>(
      'GET',
      '/user/business-units/',
    );
  },
  getCurrentSession() {
    return makeCancellableAPIRequest<UserSessionDto>(
      'GET',
      '/user/details/session',
    );
  },
  updateCurrentSession() {
    return makeCancellableAPIRequest<void>('GET', ' /user/update/session-time');
  },
  switchBusinessUnit(
    tenantId: number,
    businessUnitId: number,
    params: Record<string, unknown> = {},
  ) {
    return makeCancellableAPIRequest<UserSessionDto>(
      'GET',
      `/user/change/account/${tenantId}/${businessUnitId}`,
      { params },
    );
  },
  getAuditLog(data: {
    filters?: Record<string, unknown>;
    [key: string]: unknown;
  }) {
    let newFilter: Record<string, unknown> = data.filters ?? {};
    if (newFilter.selectedDuration) {
      newFilter = getFindingAPIDurationFilter(
        newFilter,
        newFilter.selectedDuration as string,
      );
    }
    const { foundOn: createdAt } = newFilter as {
      foundOn?: [string, string];
      createdAt?: [string, string];
    };
    const dates =
      createdAt || (newFilter.createdAt as [string, string] | undefined);
    const params = {
      ...newFilter,
      startDate: dates && moment(dates?.[0]).valueOf(),
      endDate: dates && moment(dates?.[1]).valueOf(),
      ..._.omit(data, ['filters', 'sortColumn', 'sortOrder']),
    };
    return makeCancellableAPIRequest<PageResponse<AuditLogResponseDto>>(
      'GET',
      '/user/audit/log/details/page',
      { params },
    );
  },
  getAuditLogFilters() {
    return makeCancellableAPIRequest<AuditLogFiltersResponse>(
      'GET',
      '/user/audit/log/filters',
    );
  },
  getByEmail(email: string) {
    return makeCancellableAPIRequest<UserJpaDto>(
      'GET',
      `/user/basicdetails/${email}`,
      undefined,
      false,
    );
  },
  getTopProductPreference(BUaggregation = false) {
    const path = BUaggregation
      ? '/api/businessunits/dashboard/preference/topProductList'
      : '/user/preference/topProductList';
    return makeCancellableAPIRequest<unknown>('GET', path);
  },
  updateTopProductPreference(data: unknown) {
    return makeCancellableAPIRequest<void>(
      'POST',
      '/user/preference/topProductList',
      {
        data,
      },
    );
  },
  setDefaultAccount(data: unknown) {
    return makeCancellableAPIRequest<void>(
      'POST',
      '/user/preference/defaultTenant',
      {
        data,
      },
    );
  },
  getDefaultAccount() {
    return makeCancellableAPIRequest<unknown>(
      'GET',
      '/user/preference/defaultTenant',
    );
  },
  getDefaultBU(tenantId: number) {
    return makeCancellableAPIRequest<unknown>(
      'GET',
      '/user/preference/defaultBU',
      {
        params: {
          tenantId,
        },
      },
    );
  },
  setDefaultBU(buId: number, userId: number) {
    return failSafe(
      APIClient.post('/user/preference/defaultBU', {
        buId,
        userId,
      }),
    );
  },
  getAccessTeams(params: { uuid: string }, showDefaultMessage = false) {
    return makeCancellableAPIRequest<unknown>(
      'GET',
      `/public/get-teams?uuid=${params.uuid}`,
      undefined,
      showDefaultMessage,
    );
  },
  seekAccess(data: unknown, showDefaultMessage = false) {
    return failSafe(
      APIClient.put('/public/seekAccess', data),
      showDefaultMessage,
    );
  },
  oktaLogin(email: string, showDefaultMessage = false) {
    return failSafe(
      APIClient.get(`/public/oidc/login?email=${email}`),
      showDefaultMessage,
    );
  },
  getOktaMappingForTenant() {
    return makeCancellableAPIRequest<unknown>(
      'GET',
      '/api/okta/oktaMappingForTenant',
    );
  },
  syncOktaUsers() {
    return makeCancellableAPIRequest<void>('POST', '/api/okta/usersync');
  },
  getLoginAttempts(data: {
    filters?: Record<string, unknown>;
    [key: string]: unknown;
  }) {
    let params: Record<string, unknown> = {
      ...data.filters,
      ..._.omit(data, ['filters', 'sortColumn', 'sortOrder']),
    };

    if (params.createdAt) {
      let startDate: string | undefined;
      let endDate: string | undefined;
      if ((params.createdAt as string[]).length === 1) {
        [startDate, endDate] = (
          getStartAndEndDateForQuickOption({
            label: (params.createdAt as string[])[0],
          }) as moment.Moment[]
        ).map((m) => m.toDate().toUTCString());
      } else {
        startDate = moment((params.createdAt as string[])[0])
          .toDate()
          .toUTCString();
        endDate = moment((params.createdAt as string[])[1])
          .toDate()
          .toUTCString();
      }
      params = {
        startDate,
        endDate,
        ..._.omit(params, ['createdAt']),
      };
    }

    return makeCancellableAPIRequest<PageResponse<LoginAttemptResponseDto>>(
      'GET',
      '/api/failed-login/attempts',
      {
        params,
      },
    );
  },
  getLoginAttemptsFilter() {
    return makeCancellableAPIRequest<LoginAttemptsFilterResponse>(
      'GET',
      '/api/failed-login/filters',
    );
  },
  updateLoginAttempts(id: number, params: UpdateLoginAttemptParams) {
    return makeCancellableAPIRequest<void>(
      'PUT',
      `/api/failed-login/attempts/${id}`,
      {
        params,
      },
    );
  },
  getTeamLeads(params: { teamId?: number }) {
    return makeCancellableAPIRequest<UserJpaDto[]>('GET', '/user/team-leads', {
      params,
    });
  },
  getTeams() {
    return makeCancellableAPIRequest<NameIdPair[]>('GET', '/user/get-teams');
  },
  setPrivacyStatus(bool: boolean) {
    return makeCancellableAPIRequest<UserSessionDto>(
      'POST',
      `/user/accept-reject?action=${bool}`,
    );
  },
  getPublicUser(uuid: string) {
    return makeCancellableAPIRequest<unknown>(
      'GET',
      `/public/accessrequest/email?uuid=${uuid}`,
      undefined,
      false,
    );
  },
  getTenants(params: { name?: string; page?: number; size?: number }) {
    return makeCancellableAPIRequest<PageResponse<OrganizationSessionDto>>(
      'GET',
      '/user/tenants',
      { params },
    );
  },
  getFeatureFlags() {
    return makeCancellableAPIRequest<FeatureFlagValues>(
      'GET',
      '/user/feature-flag',
    );
  },
  deleteRole(roleId: number) {
    return makeCancellableAPIRequest<void>(
      'DELETE',
      `/admin/user/role/?id=${roleId}`,
    );
  },
  updateUserPreference(data: UserPreferenceUpdateRequest) {
    return makeCancellableAPIRequest<BaseResponseDto<string>>(
      'PUT',
      '/api/v2/user/preference',
      { data },
    );
  },
  usersByAccessibleTeams(data: UsersInAccessibleTeamsRequest) {
    return makeCancellableAPIRequest<
      BaseResponseDto<PageResponse<UserResponseShort>>
    >('POST', '/api/v2/user/accessible-teams', { data });
  },
  getUserStatsMetric(data: UserStatsMetricPayload) {
    const payload = { ...data, filters: toUserStatsApiFilters(data.filters) };
    return makeCancellableAPIRequest<BaseResponseDto<UserStatsMetricResponse>>(
      'POST',
      '/api/v2/user/user-stats/metric',
      { data: payload },
    );
  },
  getUserStatsAll(data: UserStatsAllPayload) {
    const payload = { ...data, filters: toUserStatsApiFilters(data.filters) };
    return makeCancellableAPIRequest<BaseResponseDto<UserStatsAllResponse>>(
      'POST',
      '/api/v2/user/user-stats/all',
      { data: payload },
    );
  },
};

export default UserAPI;
